import { useMutation, useQueryClient } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { UPDATE_ORG_USER_ROLE_MUTATION } from '@/services/org-admin';
import type { OrgAdminUserRoleAssignment } from '@/services/org-admin';

interface UpdateOrgUserRoleResponse {
  updateOrgUserRole: OrgAdminUserRoleAssignment;
}

export interface AssignMemberRoleVariables {
  /**
   * The member's **user** id, not their membership row id.
   *
   * The retired REST route was keyed on `memberId`; this mutation is keyed on `userId`, the same key
   * `deactivateOrgUser`/`reactivateOrgUser` already use. Callers that still pass `member.id` would
   * fail server-side with "not a member of this organization", which reads like a permissions
   * problem rather than the wrong identifier — so the field name is deliberately `userId`, not
   * something a `memberId` would silently satisfy.
   */
  userId: string;
  roleId: string;
}

/**
 * Assign a role to a member.
 *
 * Named for the job rather than for the mutation, matching `useDeactivateOrgMember`, which wraps
 * `deactivateOrgUser`. Moved from `PATCH /orgs/:organizationId/members/:memberId` to the
 * `updateOrgUserRole` GraphQL mutation when the backend retired that route — see the note above the
 * mutation document in `services/org-admin/queries.ts` for why that history is worth knowing.
 *
 * The target must be an ACTIVE member and the role must belong to the org. Both are server-enforced;
 * the UI gates the controls so those errors are not the way a user discovers the rule.
 */
export function useAssignMemberRole() {
  const queryClient = useQueryClient();
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  return useMutation({
    mutationFn: async ({ userId, roleId }: AssignMemberRoleVariables) => {
      const response = await graphqlRequest<UpdateOrgUserRoleResponse>(
        UPDATE_ORG_USER_ROLE_MUTATION,
        { input: { organizationId: orgId, userId, roleId } },
      );
      return response.updateOrgUserRole;
    },
    onSuccess: () => {
      /*
       * Roles are invalidated as well as members, which the REST version did not do.
       *
       * A role card shows how many members hold it, and the Roles panel derives that count from the
       * members list — so an assignment made from a role card changed the count of two roles and
       * left both cards displaying the old numbers until something else refetched.
       */
      void queryClient.invalidateQueries({ queryKey: ['org-members', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['org-roles', orgId] });
    },
    // Caller already shows its own success/error toast — suppress the global one.
    meta: { silent: true },
  });
}
